<template>
  <div class="containers">
    <div class="header" v-if="title || tab === '' || tab === null">
      <HeaderTitle
        :title="title"
        :description="'Monitor active user sessions with longer duration'"
        :icon="'timeline'"
        :theme="'purple'"
      >
      </HeaderTitle>

      <div
        class="btn-group custom-btn-group w-100"
        v-if="0 && (tab === '' || tab === null) && !isLoading"
      >
        <button
          class="btn btn-modern d-flex align-items-center"
          @click="setView('live')"
          :class="viewMode === 'live' ? 'active' : ''"
          :disabled="isLoading"
        >
          <div class="live-indicator me-2"></div>
          Live
        </button>

        <button
          class="btn btn-modern d-flex align-items-center"
          @click="setView('notLive')"
          :class="viewMode === 'notLive' ? 'active' : ''"
          :disabled="isLoading"
        >
          <i class="fas fa-chart-bar me-2"></i>
          Offline
        </button>
      </div>
    </div>
    <div
      :class="{
        'connections-grid-full': showGridFullWidth,
        'connections-grid': !showGridFullWidth,
        'full-grid-col': tableData.length === 0,
      }"
      v-if="!isLoading"
    >
      <div
        class="connection-card"
        v-if="tableData?.length > 0"
        data-status="live"
        v-for="(row, rowIndex) in tableData"
        :key="'row-' + rowIndex"
      >
        <div class="card-header">
          <div class="left-section">
            <div class="user-info">
              <div
                class="user-device"
                :title="'Open Player Details'"
                @mousedown="openPlayerInNewTab($event, row.user_id)"
                @click="openPlayer(row.user_id)"
              >
                <i class="fa-solid fa-mobile-screen-button device-icon"></i>
                <span class="user-name">{{ row.user_id }}</span>
              </div>

              <div class="duration-info" :class="viewMode">
                <svg
                  class="clock-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12,6 12,12 16,14"></polyline>
                </svg>
                <span
                  class="duration"
                  :class="viewMode"
                  v-html="formatDuration(row)"
                ></span>
              </div>

              <div class="ip-info">
                <svg
                  class="globe-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path
                    d="m12,2a15.3,15.3 0 0 1 4,10 15.3,15.3 0 0 1 -4,10 15.3,15.3 0 0 1 -4,-10 15.3,15.3 0 0 1 4,-10z"
                  ></path>
                </svg>
                <span class="ip">{{ row.ip }}</span>
              </div>
            </div>
          </div>

          <div class="action-buttons">
            <button
              class="vedi-bets-btn"
              @click="openRounds(row)"
              @mousedown="openRoundsInNewTab($event, row)"
            >
              <IconWrapper
                :icon="'sports_soccer'"
                :text="'Rounds'"
                :theme="'orange'"
              ></IconWrapper>
            </button>
          </div>
        </div>

        <button
          class="toggle-details-btn"
          @click="toggleRow(rowIndex)"
          v-if="isMobile"
        >
          <i
            v-if="expandedRows.includes(rowIndex)"
            class="fa-solid fa-eye-slash me-1"
          ></i>

          <i v-else class="fa-solid fa-eye me-1"></i>
          {{
            expandedRows.includes(rowIndex) ? "Hide Details" : "Show Details"
          }}
        </button>

        <div
          class="card-details"
          v-show="!isMobile || expandedRows.includes(rowIndex)"
        >
          <div class="details-grid">
            <div
              class="detail-item"
              @click="copyToClipboard(row.token)"
              :title="`Click to copy Token`"
            >
              <span class="detail-label">Token:</span>
              <p class="detail-value token">{{ row.token }}</p>
            </div>
            <div class="detail-item">
              <span class="detail-label">Device:</span>
              <p class="detail-value">
                {{ row.platform }} - {{ row.browser }} ({{ row.device }})
              </p>
            </div>
            <div class="detail-item">
              <span class="detail-label">Created At:</span>
              <p class="detail-value">{{ formatUtc(row.createdAt) }}</p>
            </div>
            <div class="detail-item">
              <span class="detail-label">Last Ping:</span>
              <p class="detail-value">{{ formatUtc(row.ping_at) }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="w-100 text-white d-block text-center" v-else>
        No session found.
      </div>
    </div>
    <div
      class="position-relative d-flex justify-content-center align-items-center w-100"
      style="min-height: 350px"
      v-else
    >
      <Loader />
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getFetch } from "@/api/axiosClient";
import Loader from "./Loader.vue";
import Swal from "sweetalert2";
import Filters from "./table/Filters.vue";
import IconWrapper from "./Utility/IconWrapper.vue";
import HeaderTitle from "./Utility/HeaderTitle.vue";
import { formatString } from "@/helpers/date.js";

Chart.register(...registerables, ChartDataLabels);
export default {
  components: {
    Loader,
    Filters,
    IconWrapper,
    HeaderTitle,
  },
  props: {
    title: {
      type: String,
    },
    skin_id: {
      type: Number,
      default: null,
    },
    collapse: {
      type: Boolean,
      default: true,
    },
    filters: {
      type: Boolean,
      default: false,
    },
    tab: {
      type: String,
      default: "",
    },
    limit: {
      type: Number,
      default: 10,
    },
    player_id: {
      type: String,
      default: null,
    },
    customClass: {
      type: String,
    },
    showGridFullWidth: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      liveInterval: null,
      currentTime: Date.now(),
      expandedRows: [],
      isMobile: false,
      viewMode: "live",
      isLoading: false,
      isOpen: false,
      start_date: "",
      end_date: "",
      legendItems: [],
      selectedPeriod: "360",
      selectedOption: "360",
      filterOptions: [
        {
          label: "Start Date",
          type: "date",
          placeholder: "Start Date",
          property: "start_date",
          isDate: true,
        },
        {
          label: "End Date",
          type: "date",
          placeholder: "End Date",
          property: "end_date",
          isDate: true,
        },
      ],
      options: [
        { value: "0", label: "Today" },
        { value: "1", label: "1 Day" },
        { value: "7", label: "7 Days" },
        { value: "15", label: "15 Days" },
        { value: "30", label: "30 Days" },
        { value: "60", label: "60 Days" },
      ],
      hasData: true,
      tableColumn: [
        "user_id",
        "connection_id",
        "origin",
        "createdAt",
        "ping_at",
        "duration",
      ],
      isScrollableTop: false,
      isScrollableBottom: false,
      tableData: [],
    };
  },

  computed: {
    selectedOptionLabel() {
      const selected = this.options.find(
        (option) => option.value === this.selectedOption
      );
      return selected ? selected.label : "Select Duration";
    },
  },

  methods: {
    async search() {
      this.fetchData();
    },
    formatUtc(dateString) {
      return formatString(dateString, { format: "YYYY-MM-DD HH:mm:ss" });
    },
    openPlayerInNewTab(event, user_id) {
      if (event.button === 1) {
        event.preventDefault();
        this.openPlayer(user_id);
      }
    },
    async openPlayer(user_id) {
      let skin = user_id.split("_")[0];
      skin = Number(skin);
      if (!skin) return alert("Player Demo");
      try {
        const data = await getFetch(
          `players/detail/${user_id}?skin_id=${skin}`
        );
        if (data) {
          setTimeout(() => {
            window.open(`/players/${data._id}`, "_blank");
          });
        } else alert("Player detail are not present");
      } catch (error) {
        alert("Player detail are not present");
      }
    },
    formatData(isoString) {
      const date = new Date(isoString);

      const pad = (n) => n.toString().padStart(2, "0");

      const year = date.getUTCFullYear();
      const month = date.getUTCMonth() + 1;
      const day = date.getUTCDate();
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      return `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}`;
    },
    openRoundsInNewTab(event, row) {
      if (event.button === 1) {
        event.preventDefault();
        this.openRounds(row);
      }
    },
    openRounds(row) {
      let params = `?user_id=${row.user_id}`;

      params += `&skin_id=${row.skin_id}`;
      params += `&start_at=${this.formatData(row.createdAt)}`;
      if (this.viewMode == "notLive")
        params += `&end_at=${this.formatData(row.disconnected_at)}`;
      setTimeout(() => {
        window.open(`/rounds${params}`, "_blank");
      });
    },
    async resetFilter() {
      this.resetPagination();
      this.selectedLimit = 15;
      this.start_date = this.getInitialStartDate();
      this.end_date = this.getInitialEndDate();
      await this.search();
    },
    async resetPagination() {
      this.currentPage = 1;
    },
    copyToClipboard(value) {
      if (!value) return;
      navigator.clipboard.writeText(value).then(() => {
        Swal.fire({
          toast: true,
          position: "bottom-end",
          title: "Copied!",
          text: `${value}`,
          // icon: "success",
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
          heightAuto: false,
          customClass: {
            popup: "swal-toast-copied-info",
          },
        });
      });
    },
    startLiveTimer() {
      return;
      this.stopLiveTimer();
      if (this.viewMode === "live") {
        this.liveInterval = setInterval(() => {
          this.currentTime = Date.now();
        }, 1000);
      }
    },

    stopLiveTimer() {
      if (this.liveInterval) {
        clearInterval(this.liveInterval);
        this.liveInterval = null;
      }
    },
    toggleRow(index) {
      const rowIndex = this.expandedRows.indexOf(index);
      if (rowIndex > -1) {
        this.expandedRows.splice(rowIndex, 1);
      } else {
        this.expandedRows.push(index);
      }
    },
    formatDuration(row) {
      const hasTimestamps = row.createdAt;

      if (!hasTimestamps) return "--";

      const start = new Date(row.createdAt);
      let end;

      if (this.viewMode === "live") {
        end = new Date(this.currentTime);
      } else {
        if (!row.ping_at) return "--";
        end = new Date(row.ping_at);
      }

      const diffMs = end - start;
      const durationStr = this.msToHMS(diffMs);

      if (this.viewMode === "notLive") {
        return `<small class="disconnected-text-user fw-bold">${durationStr}</small>`;
      }

      return durationStr;
    },

    msToHMS(ms) {
      if (!ms || isNaN(ms)) return "--";

      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
      parts.push(`${seconds}s`);

      return parts.join(" ");
    },

    extractToken(origin) {
      if (!origin) return "--";

      const match = origin.match(/token=([^&]+)/);
      return match ? match[1] : "--";
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    selectOption(option) {
      this.selectedOption = option.value;
      this.isOpen = false;
      this.fetchData();
    },
    async setView(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      await this.fetchData();

      if (mode === "live") {
        this.startLiveTimer();
      } else {
        this.stopLiveTimer();
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 767;
    },
    checkScrollShadows() {
      const el = this.$refs.scrollContainerConnection;
      if (!el) return;

      this.isScrollableTop = el.scrollTop > 0;
      this.isScrollableBottom =
        el.scrollTop + el.clientHeight < el.scrollHeight;
    },
    onScroll() {
      this.checkScrollShadows();
    },
    async fetchData() {
      this.isLoading = true;
      this.hasData = false;
      this.expandedRows = [];
      try {
        let query = "?limit=" + this.limit;
        if (this.viewMode === "notLive") {
          const type = "notlive";
          const period = this.selectedOption || 0;
          query = `?type=${type}&period=${period}`;
          query += "&limit=10";
          if (this.filters)
            query += `&start_at=${this.start_date}&end_at=${this.end_date}`;

          if (this.player_id) query += `&player_id=${this.player_id}`;
        }

        if (this.skin_id) query += `&skin_id=${this.skin_id}`;

        const response = await getFetch(
          `dashboard/sessions${query}`,
          {},
          this.skin_id ? false : true
        );
        const data = response.data;
        this.$emit("totalLive", data.length);
        data.length;
        if (!data || data.length == 0) {
          this.hasData = false;
          this.isLoading = false;
          console.warn("Nessun dato ricevuto dall'API");
          return;
        } else {
          this.hasData = true;
          this.tableData = data;
        }
        this.isLoading = false;
        this.handleResize();
      } catch (error) {
        console.error("Errore nel caricamento dati:", error);
        this.isLoading = false;
        this.hasData = false;
      }

      this.$nextTick(() => {
        const el = this.$refs.scrollContainerConnection;
        if (el) {
          el.addEventListener("scroll", this.onScroll);
          this.checkScrollShadows();
        }
      });
    },

    handleClickOutside(event) {
      const selectElement = this.$refs.selectContainer;
      if (selectElement && !selectElement.contains(event.target)) {
        this.isOpen = false;
      }
    },
    getInitialStartDate() {
      return new Date().toISOString().split("T")[0];
    },
    getInitialEndDate() {
      return new Date().toISOString().split("T")[0];
    },
  },

  mounted() {
    if (this.tab) this.viewMode = this.tab;

    this.start_date = this.getInitialStartDate();
    this.end_date = this.getInitialEndDate();

    this.fetchData();
    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("resize", this.handleResize);

    this.startLiveTimer();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("click", this.handleClickOutside);
    this.stopLiveTimer();

    const el = this.$refs.scrollContainerConnection;
    if (el) {
      el.removeEventListener("scroll", this.onScroll);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/style.scss";
</style>

<style lang="scss" scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* Header Styles */
.header {
  margin-bottom: 32px;
}

/* Filter Buttons */
.filter-buttons {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #1e293b;
  color: #cbd5e1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-btn:hover {
  background: #334155;
  transform: translateY(-1px);
}

.filter-btn.active {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  color: white;
  box-shadow: 0 10px 25px rgba(59, 130, 246, 0.25);
}

.live-btn.active {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 10px 25px rgba(16, 185, 129, 0.25);
}

.live-indicator {
  width: 8px;
  height: 8px;
  background: #4ade80;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

/* Connection Cards */
.connections-grid {
  // display: flex;
  // flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }

  &.full-grid-col {
    grid-template-columns: 1fr;
  }
}

.connections-grid-full {
  gap: 16px;
  margin-bottom: 32px;
  display: grid;
  grid-template-columns: 1fr;

  &.full-grid-col {
    grid-template-columns: 1fr;
  }
}

.connection-card {
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(71, 85, 105, 0.5);
  border-radius: 16px;
  padding: 1.15rem 1.5rem;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 100%;
  display: flex;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.connection-card:hover {
  background: rgba(30, 41, 59, 0.7);
  box-shadow: 0 20px 40px rgba(139, 92, 246, 0.1);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.left-section {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  padding-right: 10px;
  max-width: 70%;

  @media (max-width: 480px) {
    max-width: 100%;
  }
}

.status-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-indicator {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  box-shadow: 0 0 10px currentColor;
}

.status-indicator.live {
  background: #4ade80;
  animation: pulse 2s infinite;
}

.status-indicator.offline {
  background: #6b7280;
}

.status-text {
  font-weight: 500;
  font-size: 14px;
}

.status-text.live {
  color: #4ade80;
}

.status-text.offline {
  color: #6b7280;
}

.user-info {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-start;

  @media (max-width: 767px) {
    flex-direction: column;
  }
}

.user-device {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    text-decoration: underline;

    .user-name {
      transform: translateY(-1px);
      text-decoration: underline;
    }
  }
}

.device-icon {
  width: 16px;
  height: 16px;
  color: #cbd5e1;
  font-size: 16px;
}

.user-name {
  font-weight: 600;
  font-size: 18px;
  color: white;
  transition: 0.3s all ease;
  overflow: hidden;
  display: block;
  text-overflow: ellipsis;
  max-width: 100%;
  width: 100%;
}

.duration-info,
.ip-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.clock-icon {
  width: 16px;
  height: 16px;
  color: rgb(199, 48, 106);
}

.duration {
  font-size: 14px;
  color: #60a5fa;
  font-weight: 500;
}

.notLive {
  color: rgb(233, 30, 108);
  font-size: 14px;
}

.duration-info.live {
  .clock-icon,
  .duration {
    color: #4ade80;
  }
}

.globe-icon {
  width: 16px;
  height: 16px;
  color: #94a3b8;
}

.ip {
  font-size: 14px;
  color: #94a3b8;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.vedi-bets-btn {
  color: white;
  border: none;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;

  @media (max-width: 480px) {
    .icon-wrapper {
      width: 100%;
      display: flex;
      min-width: 100%;
      justify-content: center;
      gap: 0.25rem;
    }
  }
}

/* Card Details */
.card-details {
  padding-top: 16px;
  border-top: 1px solid rgba(71, 85, 105, 0.5);
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.detail-item {
  font-size: 14px;
}

.detail-label {
  color: #94a3b8;
  display: block;
  margin-bottom: 2px;
}

.detail-value {
  color: #e2e8f0;
  font-weight: 500;
  margin: 0;
}

.token-section {
  margin-top: 12px;
  cursor: pointer;
}

.token-label {
  color: #94a3b8;
  font-size: 12px;
  display: block;
  margin-bottom: 4px;
}

.token-value {
  color: #64748b;
  font-family: "Courier New", monospace;
  font-size: 12px;
  word-break: break-all;
  margin: 0;
}

.stat-card {
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(71, 85, 105, 0.5);
  border-radius: 16px;
  padding: 24px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  background: rgba(30, 41, 59, 0.7);
  transform: translateY(-2px);
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-icon {
  padding: 8px;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon.green {
  background: rgba(16, 185, 129, 0.2);
}

.stat-icon.blue {
  background: rgba(59, 130, 246, 0.2);
}

.stat-icon.purple {
  background: rgba(139, 92, 246, 0.2);
}

.stat-icon svg {
  width: 20px;
  height: 20px;
}

.stat-icon.green svg {
  color: #4ade80;
}

.stat-icon.blue svg {
  color: #60a5fa;
}

.stat-icon.purple svg {
  color: #a78bfa;
}

.stat-label {
  color: #94a3b8;
  font-size: 14px;
  margin-bottom: 4px;
}

.stat-value {
  color: white;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .action-buttons {
    align-items: flex-end;

    .vedi-bets-btn {
      .text {
        display: none;
      }
    }
  }

  .filter-buttons {
    width: 100%;
  }

  .filter-btn {
    flex: 1;
    justify-content: center;
    min-width: 0;
  }

  .card-header {
    //flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .left-section {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding-right: 10px;
  }

  .user-info {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
  }

  .details-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .connection-card {
    padding: 16px;
  }

  .action-buttons {
    flex-direction: column;
    width: 100%;
  }

  .vedi-bets-btn {
    width: 100%;
    justify-content: flex-end;
  }
}

.hidden {
  display: none;
}

.toggle-details-btn {
  margin: 0px 0px 5px 0px;
  padding: 6px 12px;
  font-size: 14px;
  border: none;
  background-color: #09a6a2;
  color: white;
  border-radius: 14px;
  cursor: pointer;
  transition: 0.3s all ease;

  &:hover {
    transform: translateY(-1px);
  }
}

.btn-modern {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  padding: 0.75rem 0.75rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  font-size: clamp(13px, 1vw, 15px);

  &:hover {
    background: linear-gradient(45deg, #407aaa, #105eac);
    border-color: transparent;
    box-shadow: 0 4px 15px rgba(33, 150, 243, 0.3);
    opacity: 0.9;
  }

  &.active {
    background: linear-gradient(45deg, #2196f3, #1976d2);
    border-color: transparent;
    box-shadow: 0 4px 15px rgba(33, 150, 243, 0.3);
  }
}

.custom-btn-group {
  .btn-modern {
    font-size: clamp(13px, 1vw, 15px);

    &:first-child {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:last-child {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
}

.btn-group {
  position: relative;
  padding-top: 1.25rem;
  flex: 1 1 auto;
  color: white;
  max-width: fit-content;
}

.token {
  cursor: pointer;

  &:hover {
    color: greenyellow;
    text-decoration: underline;
    transition: 0.3s all ease;
  }
}
</style>
